import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { formatMoney, formatQty, PAYMENT_METHODS, UNITS, vatFromInclusive, type CurrencyCode, type PaymentMethod } from '@tengesa/shared';
import type { FiscalStamp, SaleDetail } from '../../lib/types';

const FISCAL_TITLE: Record<FiscalStamp['environment'], string> = {
  production: 'FISCAL TAX INVOICE',
  test: 'ZIMRA TEST RECEIPT — NOT VALID FOR TAX',
  simulator: 'SIMULATED — NOT A FISCAL RECEIPT',
};

/**
 * 80 mm till receipt. The same component is shown on screen and printed: the print
 * stylesheet hides everything except the element with the receipt-print class.
 */
export function Receipt({ sale, copy }: { sale: SaleDetail; copy?: boolean }) {
  const cur = sale.currency as CurrencyCode;
  const m = (c: number) => formatMoney(c, cur, { symbol: false });
  const b = sale.business;
  const vatByRate = new Map<number, { total: number; vat: number }>();
  for (const l of sale.lines) {
    const r = vatByRate.get(l.vatRateBp) ?? { total: 0, vat: 0 };
    r.total += l.netCents;
    vatByRate.set(l.vatRateBp, r);
  }
  // VAT per rate on the total at that rate, as on the fiscal receipt.
  for (const [rate, r] of vatByRate) r.vat = vatFromInclusive(r.total, rate);
  const fiscal = sale.fiscal.find((f) => f.receiptType === 'FiscalInvoice') ?? null;
  const creditNotes = sale.fiscal.filter((f) => f.receiptType === 'CreditNote');
  const when = new Intl.DateTimeFormat('en-GB', { timeZone: 'Africa/Harare', dateStyle: 'short', timeStyle: 'short' }).format(new Date(sale.createdAt));
  return (
    <div className="receipt-print mx-auto w-full max-w-[302px] bg-white p-4 font-mono text-[12px] leading-[1.45] text-black">
      <div className="text-center">
        {(b.receiptHeader ?? b.tradingName ?? b.name).split('\n').map((l, i) => (
          <p key={i} className={i === 0 ? 'text-[14px] font-bold' : ''}>
            {l}
          </p>
        ))}
        {b.tradingName && b.receiptHeader && <p>{b.name}</p>}
        <p>{sale.branch.name} branch</p>
        {(sale.branch.address ?? b.address) && <p>{sale.branch.address ?? b.address}</p>}
        {(sale.branch.phone ?? b.phone) && <p>Tel {sale.branch.phone ?? b.phone}</p>}
        {b.tin && <p>TIN {b.tin}</p>}
        {b.vatRegistered && b.vatNumber && <p>VAT No. {b.vatNumber}</p>}
        <p className="mt-1 font-bold">
          {fiscal ? FISCAL_TITLE[fiscal.environment] : b.vatRegistered ? 'TAX INVOICE' : 'RECEIPT'}
          {copy ? ' (COPY)' : ''}
        </p>
      </div>
      <Rule />
      <p>Receipt {sale.number}</p>
      <p>{when}</p>
      <p>Cashier {sale.cashier.name}</p>
      {sale.customer && <p>Customer {sale.customer.name}</p>}
      {sale.status === 'voided' && <p className="mt-1 font-bold">*** VOIDED — {sale.voidReason} ***</p>}
      <Rule />
      {sale.lines.map((l) => (
        <div key={l.id} className="mb-1">
          <p>{l.productName}</p>
          <div className="flex justify-between">
            <span>
              {formatQty(l.qtyMilli)}
              {UNITS[l.unit as keyof typeof UNITS]?.decimals ? UNITS[l.unit as keyof typeof UNITS].short : ''} x {m(l.unitPriceCents)}
              {l.vatRateBp === 0 && b.vatRegistered ? ' Z' : ''}
            </span>
            <span>{m(l.grossCents)}</span>
          </div>
          {l.promoDiscountCents + l.manualDiscountCents > 0 && (
            <div className="flex justify-between">
              <span>  Discount</span>
              <span>-{m(l.promoDiscountCents + l.manualDiscountCents)}</span>
            </div>
          )}
        </div>
      ))}
      {sale.lines.some((l) => l.saleDiscountCents > 0) && (
        <div className="flex justify-between">
          <span>Sale discount</span>
          <span>-{m(sale.lines.reduce((a, l) => a + l.saleDiscountCents, 0))}</span>
        </div>
      )}
      <Rule />
      <div className="flex justify-between text-[14px] font-bold">
        <span>TOTAL {cur}</span>
        <span>{m(sale.totalCents)}</span>
      </div>
      {sale.payments.map((p) => (
        <div key={p.id} className="flex justify-between">
          <span>
            {PAYMENT_METHODS[p.method as PaymentMethod]?.label.split(' (')[0] ?? p.method}
            {p.currency !== cur ? ` ${p.currency} @${p.rate}` : ''}
          </span>
          <span>{formatMoney(p.amountCents, p.currency as CurrencyCode, { symbol: false })}</span>
        </div>
      ))}
      {sale.changeCents > 0 && (
        <div className="flex justify-between">
          <span>Change {sale.changeCurrency ?? cur}</span>
          <span>{formatMoney(sale.changeCents, (sale.changeCurrency ?? cur) as CurrencyCode, { symbol: false })}</span>
        </div>
      )}
      {b.vatRegistered && (
        <>
          <Rule />
          {[...vatByRate.entries()]
            .sort((a, z) => z[0] - a[0])
            .map(([rate, v]) => (
              <div key={rate} className="flex justify-between">
                <span>{rate === 0 ? 'Zero-rated/exempt' : `VAT ${(rate / 100).toFixed(rate % 100 ? 1 : 0)}%`} on {m(v.total)}</span>
                <span>{m(v.vat)}</span>
              </div>
            ))}
        </>
      )}
      {fiscal && (
        <>
          <Rule />
          <FiscalBlock stamp={fiscal} />
        </>
      )}
      {sale.returns.length > 0 && (
        <>
          <Rule />
          {sale.returns.map((r) => (
            <div key={r.id}>
              <p>
                Returned {r.number}: -{m(r.refundBaseCents)}
              </p>
              {creditNotes.filter((c) => c.returnId === r.id).map((c) => (
                <p key={c.id}>
                  Credit note {c.receiptGlobalNo}, code {c.verificationCode}
                </p>
              ))}
            </div>
          ))}
        </>
      )}
      <Rule />
      <div className="text-center">
        {(b.receiptFooter ?? 'Thank you!').split('\n').map((l, i) => (
          <p key={i}>{l}</p>
        ))}
      </div>
    </div>
  );
}

/** Device, numbers, verification code and the QR customers (and ZIMRA) scan to check the receipt. */
function FiscalBlock({ stamp }: { stamp: FiscalStamp }) {
  const [svg, setSvg] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    QRCode.toString(stamp.qrData, { type: 'svg', margin: 0, errorCorrectionLevel: 'M' })
      .then((s) => live && setSvg(s))
      .catch(() => live && setSvg(null));
    return () => {
      live = false;
    };
  }, [stamp.qrData]);
  const when = stamp.receiptDate.replace('T', ' ');
  return (
    <div className="text-center">
      {stamp.environment !== 'production' && <p className="font-bold">{FISCAL_TITLE[stamp.environment]}</p>}
      <p>
        Fiscal day {stamp.fiscalDayNo} · Receipt {stamp.receiptCounter}/{stamp.receiptGlobalNo}
      </p>
      <p>Device {stamp.deviceId} · S/N {stamp.deviceSerialNo}</p>
      <p>{when}</p>
      {svg && <div className="mx-auto my-2 w-32" aria-label="Verification QR code" role="img" dangerouslySetInnerHTML={{ __html: svg }} />}
      <p className="font-bold">Verification code</p>
      <p className="text-[14px] font-bold tracking-wider">{stamp.verificationCode}</p>
      <p className="break-all text-[10px]">{stamp.qrData.replace(/^(https?:\/\/[^/]+\/).*$/, 'Verify at $1')}</p>
    </div>
  );
}

function Rule() {
  return <div className="my-1.5 border-t border-dashed border-black" aria-hidden />;
}

export function printReceipt() {
  window.print();
}
